import { Box, CardActionArea, CardMedia, Grid, Stack } from '@mui/material';
import { AppImageDimension } from 'app/appImages';
import { MprcClickable, MprcIconButtonMoreOptionsDropdown, MprcText } from "components";
import { MprcCardTypography } from 'components/MprcCardTypography';
import { CardAction, CardActionLocation, CardActionType, CardDto, CardText, CardTextVariant } from "generated/CoreApi_v1";
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { CardRenderActions } from 'views/general/Cards/CardDtoActions';
import { discoverPath, ICardDtoToCardRibbonOptions, RenderCardIcon, RenderCardSticker, RenderCardText } from 'views/general/Cards/Common';
import { DeleteDevPlanItem } from 'views/lms/devPlan/Home/Edit/EditDevPlanView/DeleteDevPlanItem';
import { DevPlanReorderItemsDialog } from 'views/lms/devPlan/Home/Edit/EditDevPlanView/DevPlanReorderItemsDialog';
import { EditDevPlanItemDialog } from 'views/lms/devPlan/Home/Edit/EditDevPlanView/EditDevPlanItem';
import { EditProgressBox, UpdateOtherActivityProgress } from 'views/lms/devPlan/Home/Edit/EditDevPlanView/EditProgressBox';
import { ActivityViewer } from 'views/lms/devPlan/publication/ActivityViewer';
import { CreateDevPlanActivity } from 'views/lms/devPlan/publication/CreateActivity';

///////////////////////////////////////////////////////////////////////////////

const Title = ({ item, onReload }: { item: CardDto; onReload?: () => void; }) => {

    var lines = item?.lines.filter((el, i) => i < 2) ?? [];
    return BodyLines(item, lines, onReload);
};

const Subtitle = (item: CardDto) => {

    var lines = item?.lines.filter((el, i) => i > 1) ?? [];
    return BodyLines(item, lines);
};

const BodyLines = (item: CardDto, lines: CardText[], onReload?: () => void) => {

    const navigate = useNavigate();
    const titleAction = item.actions.find(p => p.location == CardActionLocation.Title);

    const [dialog, setDialog] = useState<any>();

    const handleClick = (line: CardText) => {

        const titleLink = titleAction && discoverPath(titleAction, item.data)?.url;

        if (!titleLink) {
            if (titleAction?.actionType == CardActionType.DevPlanActivityEdit) {
                setDialog(
                    <CreateDevPlanActivity
                        devPlanGroupId={item.data["devPlanGroupId"]}
                        publicationId={item.data["publicationId"]}
                        onClose={(success) => {
                            if (success) onReload && onReload();
                            setDialog(null);
                        }}
                    />
                );
            }
        }
        else navigate(titleLink);
    };

    return <Box>
        {dialog}
        {
            lines.map((line, index) => {
                const textVariant = CardTextVariant[line.variant].toCamelcase() as any;

                const renderLine = (
                    <MprcCardTypography key={index} variant={textVariant}>
                        {
                            titleAction && line.variant == CardTextVariant.Title && !item.disabled ?
                                <MprcClickable onClick={() => handleClick(line)}>
                                    <RenderCardText line={line} />
                                </MprcClickable>
                                : <RenderCardText line={line} />
                        }
                    </MprcCardTypography>

                );

                return line.variant == CardTextVariant.Sticker ? <Box width={150}>{renderLine}</Box> : renderLine;
            })
        }
    </Box>;
};

///////////////////////////////////////////////////////////////////////////////

const Actions = ({ item, onReload }: { item: CardDto; onReload?: () => void; }) => {

    const [action, setAction] = useState<any>();

    const data = item.data;
    const primaryAction = item.actions.find(p => p.location == CardActionLocation.Primary);
    const overflowActions = item.actions.filter(p => p.location == CardActionLocation.Overflow);

    const handleSelection = (action: CardAction) => {
        switch (action.actionType) {
            case CardActionType.ActionSetDeadline:
                setAction(
                    <EditDevPlanItemDialog
                        devPlanId={data["devPlanId"]}
                        devPlanGroupId={data["devPlanGroupId"]}
                        devPlanItemId={data["devPlanItemId"]}
                        type={data["type"]}
                        progress={data["progressPercent"]}
                        deadline={data["completionDeadline"]}
                        isDraft={data["isDraft"]}
                        title={item.title}
                        onClose={(success) => {
                            if (success && onReload) onReload();
                            setAction(null);
                        }}
                    />
                );
                break;
            case CardActionType.Reorder:
                setAction(
                    <DevPlanReorderItemsDialog
                        devPlanGroupId={data["devPlanGroupId"]}
                        subtitle={data["name"] ?? undefined}
                        onClose={(success) => {
                            if (success && onReload) onReload();
                            setAction(null);
                        }}
                    />
                );
                break;
            case CardActionType.DevPlanActivityView:
                setAction(
                    <ActivityViewer
                        publicationId={data["publicationId"]}
                        onClose={() => setAction(null)}
                    />
                );
                break;
            case CardActionType.Remove:
                setAction(
                    <DeleteDevPlanItem
                        devPlanItemId={data["devPlanItemId"]}
                        onClose={(success) => {
                            if (success && onReload) onReload();
                            setAction(null);
                        }}
                    />
                );
                break;
            case CardActionType.DevPlanActivityResetProgress:
                setAction(
                    <UpdateOtherActivityProgress
                        devPlanItemId={data["devPlanItemId"]}
                        progress={0}
                        onClose={(success) => {
                            if (success && onReload) onReload();
                            setAction(null);
                        }}
                    />
                );
                break;
            default:
                setAction(
                    <CardRenderActions
                        item={item}
                        cardAction={action}
                        endAction={() => setAction(null)}
                    />
                );
                break;
        }
    };

    return (
        <>
            {action}
            <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" sx={{ height: 32 }}>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid>
                        {
                            primaryAction &&
                            <MprcCardTypography variant="linkAlt" fontSize={theme => theme.typography.fontSize} color={item.disabled || primaryAction.actionType == CardActionType.None ? "muted" : undefined}>
                                <RenderCardIcon icon={primaryAction.icon} variant="pre" />
                                <MprcClickable onClick={() => handleSelection(primaryAction)} disabled={item.disabled || primaryAction.actionType == CardActionType.None}>
                                    {primaryAction.text}
                                </MprcClickable>
                            </MprcCardTypography>
                        }
                    </Grid>
                    {
                        overflowActions.length > 0 &&
                        <Grid>
                            <MprcIconButtonMoreOptionsDropdown
                                color="muted"
                                onChange={(selectedValue) => handleSelection({ actionType: selectedValue } as CardAction)}
                                items={overflowActions.map(a => ({
                                    text: a.actionType == CardActionType.Remove ? <MprcText color="error">{a.text}</MprcText> : a.text,
                                    value: a.actionType,
                                    group: a.actionType == CardActionType.Remove ? 2 : 1
                                }))}
                            />
                        </Grid>
                    }
                </Grid>

            </Stack>
        </>
    );
};

///////////////////////////////////////////////////////////////////////////////

const Thumbnail = ({ item, onReload }: { item: CardDto; onReload?: () => void; }) => {

    const navigate = useNavigate();
    const [dialog, setDialog] = useState<any>();

    if (item.thumbnail == null) return null;
    const thumbnailAction = item.actions.find(p => p.location == CardActionLocation.Thumbnail);
    const thumbnailLink = !item.disabled && thumbnailAction != undefined ? discoverPath(thumbnailAction, item.data)?.url : undefined;

    const handleClick = () => {
        if (!thumbnailLink) {
            if (thumbnailAction?.actionType == CardActionType.DevPlanActivityEdit) {
                setDialog(
                    <CreateDevPlanActivity
                        devPlanGroupId={item.data["devPlanGroupId"]}
                        publicationId={item.data["publicationId"]}
                        onClose={(success) => {
                            if (success) onReload && onReload();
                            setDialog(null);
                        }}
                    />
                );
            }
        }

        else navigate(thumbnailLink);
    };

    return <>
        {dialog}
        {thumbnailAction ?
            <CardActionArea onClick={handleClick}>
                <CardMedia
                    component="img"
                    width={AppImageDimension.ThumbnailLg.width}
                    height={AppImageDimension.ThumbnailLg.height}
                    image={item.thumbnail.url}
                />
            </CardActionArea>
            :
            <CardMedia
                component="img"
                width={AppImageDimension.ThumbnailLg.width}
                height={AppImageDimension.ThumbnailLg.height}
                image={item.thumbnail.url}
                sx={{ borderRadius: "8px" }}
            />
        }
    </>;
};

///////////////////////////////////////////////////////////////////////////////

const RenderCardMedia = (item: CardDto) => {
    return item.stickerLeft || item.stickerRight
        ? <Box sx={{ position: "relative" }}>
            <Thumbnail item={item} />
            <Box sx={{ position: "absolute", bottom: 0, left: 0, width: "100%" }}>
                <Grid container justifyContent="center" width="100%">
                    <Grid size={{ xs: 6 }}>
                        {
                            item.stickerLeft ?
                                <RenderCardSticker
                                    item={item.stickerLeft}
                                />
                                : null
                        }</Grid>
                    <Grid size={{ xs: 6 }}>
                        {
                            item.stickerRight
                                ? <RenderCardSticker
                                    item={item.stickerRight}
                                />
                                : null
                        }
                    </Grid>
                </Grid>
            </Box>
        </Box>
        : <Thumbnail item={item} />;
};

///////////////////////////////////////////////////////////////////////////////

const BuildDevPlanComponentCards = ({ item, readonly, onReload }: { item: CardDto, readonly: boolean, onReload?: () => void; }) => {

    const secondaryAction = item.actions.find(p => p.location == CardActionLocation.Secondary);

    return (
        <Grid container spacing={2} wrap="nowrap" width={"100%"}>
            <Grid sx={{ minWidth: AppImageDimension.ThumbnailLg.width }}>
                <RenderCardMedia {...item} />
            </Grid>
            <Grid size="grow">
                <Stack direction="column" justifyContent="space-between" sx={{ height: AppImageDimension.ThumbnailLg.height }}>
                    <Title item={item} onReload={onReload} />
                    <Stack direction="row" justifyContent="space-between" alignItems="end" height="80px">
                        <Stack justifyContent="space-between" height="100%">
                            <Subtitle {...item} />
                            <Actions item={item} onReload={onReload} />
                        </Stack>
                        {secondaryAction != null &&
                            <EditProgressBox
                                currentProgress={item.data["progressPercent"]}
                                devPlanItemId={item.data["devPlanItemId"]}
                                readonly={readonly}
                                onReload={onReload}
                            />
                        }
                    </Stack>
                </Stack>
            </Grid>
        </Grid>
    );
};

///////////////////////////////////////////////////////////////////////////////

export function CardDtoToDevPlanItemCardRibbon(items: CardDto[] | undefined, readonly: boolean, options?: ICardDtoToCardRibbonOptions) {
    return items == undefined || items.length == 0
        ? null
        : items.map((item) => (<BuildDevPlanComponentCards key={item.uniqueId} item={item} readonly={readonly} onReload={options?.onCarouselReload} />));
}
